import React, { useState, useEffect } from 'react';
import { Button, Input, Pagination } from '@/components/atoms/index';
import { Icon } from '@/icons';
import WordTableList from '@/components/organisms/Table/AcademyWordTable';
import { academyWordApi, adminWordApi } from '@/services/api';
import { message } from 'antd';

interface WordData {
  id: string;
  wordCode: string;
  word: string;
  meaning: string;
  ttsType: string;
  isPublic: boolean;
  registrant: string;
  createdAt: string;
  uploadStatus: 'completed' | 'failed';
}

interface WordSelectionContentProps {
  onOk: (selectedWords: WordData[]) => void;
  onCancel: () => void;
  availableWords?: WordData[];
  title?: string;
  useAdminApi?: boolean;
  academyCode?: string;
}

const WordSelectionContent: React.FC<WordSelectionContentProps> = ({
  onOk,
  onCancel,
  availableWords = [],
  title = '단어 선택',
  useAdminApi = false,
  academyCode = '',
}) => {
  const [selectedWords, setSelectedWords] = useState<WordData[]>([]);
  const [searchText, setSearchText] = useState('');
  const [filteredAvailableWords, setFilteredAvailableWords] = useState<WordData[]>([]);
  const [apiRows, setApiRows] = useState<WordData[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // 사용 가능한 단어 목록 (props 없으면 API 사용)
  const useApi = useAdminApi || availableWords.length === 0;

  useEffect(() => {
    if (useApi) return;
    const lowered = searchText.toLowerCase();
    if (!lowered.trim()) {
      setFilteredAvailableWords(availableWords);
    } else {
      const filtered = availableWords.filter(word =>
        word.word.toLowerCase().includes(lowered) ||
        word.meaning.toLowerCase().includes(lowered)
      );
      setFilteredAvailableWords(filtered);
    }
  }, [useApi, availableWords, searchText]);

  useEffect(() => {
    if (!useApi) return;
    let cancelled = false;
    const fetch = async () => {
      try {
        const params: any = { q: searchText, page: currentPage - 1, size: pageSize };
        if (!useAdminApi && academyCode) {
          params.academyCode = academyCode;
        }
        const api = useAdminApi ? adminWordApi : academyWordApi;
        const res = await api.list(params);
        const pageData = res?.data || res;
        const content = pageData?.content || pageData?.page?.content || pageData?.data?.content || [];
        const totalElements =
          pageData?.totalElements ??
          pageData?.page?.totalElements ??
          pageData?.data?.totalElements ??
          pageData?.total ??
          content.length;
        const mapped: WordData[] = content.map((item: any) => ({
          id: String(item.id),
          wordCode: item.code || '',
          word: item.word,
          meaning: item.meaning,
          ttsType: `${item.gender}/${item.age}/${item.speed}/${item.pitch}`,
          isPublic: !!item.isPublic,
          registrant: item.registrant || '',
          createdAt: (item.createdAt || '').toString().replace('T', ' ').substring(0, 16),
          uploadStatus: 'completed',
        }));
        if (!cancelled) {
          setApiRows(mapped);
          setTotal(typeof totalElements === 'number' ? totalElements : content.length);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('단어 목록을 불러오지 못했습니다.', error);
          message.error('단어 목록을 불러오지 못했습니다.');
        }
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, [useApi, useAdminApi, searchText, currentPage, academyCode]);

  useEffect(() => {
    const totalItems = useApi ? total : filteredAvailableWords.length;
    if (totalItems <= 0) {
      if (currentPage !== 1) setCurrentPage(1);
      return;
    }
    const maxPage = Math.ceil(totalItems / pageSize);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage || 1);
    }
  }, [useApi, total, filteredAvailableWords.length, currentPage]);

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const pageData = useApi ? apiRows : filteredAvailableWords.slice(startIndex, endIndex);

  // 헤더 체크박스 상태 계산
  const selectedIds = new Set(selectedWords.map(w => w.id));
  const selectedOnPageCount = pageData.reduce((acc, w) => acc + (selectedIds.has(w.id) ? 1 : 0), 0);
  const allCheckedOnPage = pageData.length > 0 && selectedOnPageCount === pageData.length;
  const indeterminateOnPage = selectedOnPageCount > 0 && selectedOnPageCount < pageData.length;

  const handleWordSelection = (wordId: string, checked: boolean) => {
    if (checked) {
      const source = pageData;
      const word = source.find(w => w.id === wordId);
      if (word && !selectedWords.find(w => w.id === wordId)) {
        setSelectedWords(prev => [...prev, word]);
      }
    } else {
      setSelectedWords(prev => prev.filter(w => w.id !== wordId));
    }
  };

  const handleToggleAllOnPage = (checked: boolean) => {
    if (checked) {
      const toAdd = pageData.filter(w => !selectedIds.has(w.id));
      if (toAdd.length > 0) setSelectedWords(prev => [...prev, ...toAdd]);
    } else {
      const pageIds = new Set(pageData.map(w => w.id));
      setSelectedWords(prev => prev.filter(w => !pageIds.has(w.id)));
    }
  };

  const handleConfirm = () => {
    setTimeout(() => {
      onOk(selectedWords);
    }, 200);
  };

  const handleCancel = () => {
    setTimeout(() => {
      onCancel();
    }, 200);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>

      <div className="mb-4">
        <Input
          placeholder="단어나 뜻으로 검색"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
          icon={<Icon name="search" size={20} />}
          className="w-full"
        />
      </div>

      <div className="mb-4">
        <WordTableList
          data={pageData}
          type="wordBookSelect"
          showCheckbox={true}
          selectedIds={selectedWords.map(w => w.id)}
          onSelectionChange={handleWordSelection}
          headerCheckbox={{
            checked: allCheckedOnPage,
            indeterminate: indeterminateOnPage,
            onChange: handleToggleAllOnPage,
          }}
          noResolver={(index) => ((useApi ? total : filteredAvailableWords.length) - ((currentPage - 1) * pageSize + index))}
        />
      </div>
      <Pagination
        total={useApi ? total : filteredAvailableWords.length}
        pageSize={pageSize}
        current={currentPage}
        onChange={(p) => setCurrentPage(p)}
      />
      <div className="flex gap-2 justify-end pt-4">
        <Button
          intent="secondary"
          onClick={handleCancel}
          className="min-w-[100px]"
        >
          취소
        </Button>
        <Button
          onClick={handleConfirm}
          className="min-w-[100px]"
        >
          선택 완료
        </Button>
      </div>
    </div>
  );
};

export default WordSelectionContent; 